import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import {
    ITask,
    TaskDetailResponse,
    TaskActivityMetrics,
    TaskDailyActivity,
    TaskExecution,
    TaskRecentDocument,
    TaskExperimentInfo,
    ActiveExperimentSummary
} from './types/task';
import './TaskDetailView.css';

const TaskDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [task, setTask] = useState<ITask | null>(null);
    const [experiment, setExperiment] = useState<TaskExperimentInfo | null>(null);
    const [activeExperiments, setActiveExperiments] = useState<ActiveExperimentSummary[]>([]);
    const [activityMetrics, setActivityMetrics] = useState<TaskActivityMetrics | null>(null);
    const [dailyActivity, setDailyActivity] = useState<TaskDailyActivity[]>([]);
    const [recentExecutions, setRecentExecutions] = useState<TaskExecution[]>([]);
    const [recentDocuments, setRecentDocuments] = useState<TaskRecentDocument[]>([]);
    const [elkAvailable, setElkAvailable] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'executions' | 'documents'>('overview');

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    const fetchTaskDetails = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks/${id}/details`, {
                headers
            });

            if (!response.ok) {
                throw new Error('Failed to fetch task details');
            }

            const data: TaskDetailResponse = await response.json();
            setTask(data.task);
            setExperiment(data.experiment || null);
            setActiveExperiments(data.activeExperiments || []);
            setActivityMetrics(data.activityMetrics);
            setDailyActivity(data.dailyActivity);
            setRecentExecutions(data.recentExecutions);
            setRecentDocuments(data.recentDocuments);
            setElkAvailable(data.elkAvailable);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id, token, apiBaseUrl, apiKey]);

    useEffect(() => {
        fetchTaskDetails();
    }, [fetchTaskDetails]);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(fetchTaskDetails, 30000);
        return () => clearInterval(interval);
    }, [fetchTaskDetails]);

    const formatDuration = (ms: number): string => {
        if (ms < 1000) return `${Math.round(ms)}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleString();
    };

    const formatRelativeTime = (dateStr: string): string => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('taskDetail.justNow');
        if (diffMins < 60) return t('taskDetail.minutesAgo', { count: diffMins });
        if (diffHours < 24) return t('taskDetail.hoursAgo', { count: diffHours });
        return t('taskDetail.daysAgo', { count: diffDays });
    };

    if (user?.role !== 'ADMIN') {
        return (
            <div className="task-detail-view">
                <div className="access-denied">
                    <span className="material-icons">lock</span>
                    <h2>{t('tasks.accessDenied')}</h2>
                    <p>{t('tasks.adminOnly')}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="task-detail-view">
                <div className="loading-container">
                    <div className="tasks-loader-flex">
                        <span className="material-icons loader-icon rotating">sync</span>
                        <span className="loader-text">{t('loading.taskDetails')}</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !task) {
        return (
            <div className="task-detail-view">
                <div className="error-container">
                    <span className="material-icons">error</span>
                    <h2>{t('taskDetail.errorLoading')}</h2>
                    <p>{error || t('taskDetail.taskNotFound')}</p>
                    <button className="btn-secondary" onClick={() => navigate('/tasks')}>
                        <span className="material-icons">arrow_back</span>
                        {t('taskDetail.backToTasks')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="task-detail-view">
            {/* Header */}
            <div className="task-detail-header">
                <button className="back-btn" onClick={() => navigate('/tasks')}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <div className="header-info">
                    <h1>{task.name}</h1>
                    <div className="header-meta">
                        <span className={`status-badge ${task.status.toLowerCase()}`}>
                            {task.status}
                        </span>
                        <span className="group-badge">
                            <span className="material-icons">folder</span>
                            {task.group || t('taskDetail.noGroup')}
                        </span>
                        {task.is_running && (
                            <span className="running-badge">
                                <span className="material-icons rotating">sync</span>
                                {t('taskDetail.running')}
                            </span>
                        )}
                    </div>
                </div>
                <div className="header-actions">
                    <button className="btn-secondary" onClick={fetchTaskDetails}>
                        <span className="material-icons">refresh</span>
                        {t('buttons.refresh')}
                    </button>
                    {task.logs_url && (
                        <a
                            href={task.logs_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary"
                        >
                            <span className="material-icons">open_in_new</span>
                            {t('taskDetail.viewLogs')}
                        </a>
                    )}
                </div>
            </div>

            {/* Metrics Summary Cards */}
            <div className="metrics-cards">
                <div className="metric-card">
                    <div className="metric-icon">
                        <span className="material-icons">description</span>
                    </div>
                    <div className="metric-content">
                        <span className="metric-value">
                            {activityMetrics?.totalProcessed.toLocaleString() || 0}
                        </span>
                        <span className="metric-label">{t('taskDetail.totalProcessed')}</span>
                    </div>
                </div>
                <div className="metric-card success">
                    <div className="metric-icon">
                        <span className="material-icons">check_circle</span>
                    </div>
                    <div className="metric-content">
                        <span className="metric-value">
                            {activityMetrics?.successRate.toFixed(1) || 0}%
                        </span>
                        <span className="metric-label">{t('taskDetail.successRate')}</span>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon">
                        <span className="material-icons">timer</span>
                    </div>
                    <div className="metric-content">
                        <span className="metric-value">
                            {formatDuration(activityMetrics?.averageDuration || 0)}
                        </span>
                        <span className="metric-label">{t('taskDetail.avgDuration')}</span>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon">
                        <span className="material-icons">schedule</span>
                    </div>
                    <div className="metric-content">
                        <span className="metric-value">
                            {task.last_run ? formatRelativeTime(task.last_run) : t('taskDetail.never')}
                        </span>
                        <span className="metric-label">{t('taskDetail.lastRun')}</span>
                    </div>
                </div>
            </div>

            {/* ELK Status Banner */}
            {!elkAvailable && (
                <div className="info-banner">
                    <span className="material-icons">info</span>
                    <span>{t('taskDetail.elkNotAvailable')}</span>
                </div>
            )}

            {/* Tabs */}
            <div className="detail-tabs">
                <button
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    <span className="material-icons">dashboard</span>
                    {t('taskDetail.tabs.overview')}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'executions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('executions')}
                >
                    <span className="material-icons">history</span>
                    {t('taskDetail.tabs.executions')}
                    {recentExecutions.length > 0 && (
                        <span className="tab-count">{recentExecutions.length}</span>
                    )}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('documents')}
                >
                    <span className="material-icons">article</span>
                    {t('taskDetail.tabs.documents')}
                    {recentDocuments.length > 0 && (
                        <span className="tab-count">{recentDocuments.length}</span>
                    )}
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        {/* Task Configuration */}
                        <div className="detail-section">
                            <h3>
                                <span className="material-icons">settings</span>
                                {t('taskDetail.configuration')}
                            </h3>
                            <div className="config-grid">
                                <div className="config-item">
                                    <span className="config-label">{t('taskDetail.batchSize')}</span>
                                    <span className="config-value">{task.batch_size}</span>
                                </div>
                                <div className="config-item">
                                    <span className="config-label">{t('taskDetail.waitTime')}</span>
                                    <span className="config-value">{task.wait_time}s</span>
                                </div>
                                <div className="config-item">
                                    <span className="config-label">{t('taskDetail.provider')}</span>
                                    <span className="config-value">{task.provider}</span>
                                </div>
                                <div className="config-item">
                                    <span className="config-label">{t('taskDetail.pipelineOrder')}</span>
                                    <span className="config-value">{task.pipeline_order}</span>
                                </div>
                                <div className="config-item">
                                    <span className="config-label">{t('taskDetail.retryOnErrors')}</span>
                                    <span className="config-value">
                                        {task.retry_on_errors ? t('common.yes') : t('common.no')}
                                    </span>
                                </div>
                            </div>
                            {task.description && (
                                <div className="task-description">
                                    <span className="config-label">{t('taskDetail.description')}</span>
                                    <p>{task.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Experiment Info */}
                        {experiment && (
                            <div className="detail-section">
                                <h3>
                                    <span className="material-icons">science</span>
                                    {t('taskDetail.experiment')}
                                </h3>
                                <div className="experiment-info">
                                    <div className="experiment-header">
                                        <span className="experiment-id">{experiment.experiment_id}</span>
                                        {experiment.provider && experiment.model && (
                                            <span className="experiment-model">
                                                {experiment.provider} / {experiment.model}
                                            </span>
                                        )}
                                    </div>
                                    <div className="experiment-metrics">
                                        <div className="exp-metric">
                                            <span className="exp-metric-value">
                                                {experiment.number_of_processed_documents.toLocaleString()}
                                            </span>
                                            <span className="exp-metric-label">
                                                {t('taskDetail.docsProcessed')}
                                            </span>
                                        </div>
                                        {experiment.overall_average_confidence !== undefined && (
                                            <div className="exp-metric">
                                                <span className="exp-metric-value">
                                                    {(experiment.overall_average_confidence * 100).toFixed(1)}%
                                                </span>
                                                <span className="exp-metric-label">
                                                    {t('taskDetail.avgConfidence')}
                                                </span>
                                            </div>
                                        )}
                                        {experiment.overall_validation_rate !== undefined && (
                                            <div className="exp-metric">
                                                <span className="exp-metric-value">
                                                    {(experiment.overall_validation_rate * 100).toFixed(1)}%
                                                </span>
                                                <span className="exp-metric-label">
                                                    {t('taskDetail.validationRate')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {experiment.property_success_rates && (
                                        <div className="property-rates">
                                            <span className="rates-title">{t('taskDetail.fieldSuccessRates')}</span>
                                            <div className="rates-grid">
                                                {Object.entries(experiment.property_success_rates).map(([field, rate]) => (
                                                    <div key={field} className="rate-item">
                                                        <span className="rate-label">{field}</span>
                                                        <div className="rate-bar">
                                                            <div
                                                                className="rate-fill"
                                                                style={{ width: `${(rate || 0) * 100}%` }}
                                                            />
                                                        </div>
                                                        <span className="rate-value">
                                                            {((rate || 0) * 100).toFixed(0)}%
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Active Experiments for Task Group */}
                        {activeExperiments.length > 0 && (
                            <div className="detail-section">
                                <h3>
                                    <span className="material-icons">science</span>
                                    {t('taskDetail.activeExperiments')}
                                    <span className="badge badge-info">{activeExperiments.length}</span>
                                </h3>
                                <div className="active-experiments-list">
                                    {activeExperiments.map((exp) => (
                                        <div
                                            key={exp._id}
                                            className="active-experiment-item"
                                            onClick={() => navigate(`/experiments/${exp._id}`)}
                                        >
                                            <div className="exp-item-header">
                                                <span className="exp-item-name">{exp.name || exp.experiment_id}</span>
                                                <span className={`status-badge status-${exp.status || 'draft'}`}>
                                                    {exp.status || 'draft'}
                                                </span>
                                            </div>
                                            <div className="exp-item-details">
                                                {exp.provider && exp.model && (
                                                    <span className="exp-item-model">
                                                        <span className="material-icons">memory</span>
                                                        {exp.provider} / {exp.model}
                                                    </span>
                                                )}
                                                <span className="exp-item-docs">
                                                    <span className="material-icons">description</span>
                                                    {exp.processedDocs.toLocaleString()} {t('taskDetail.docs')}
                                                </span>
                                                {exp.validationRate !== undefined && (
                                                    <span className="exp-item-rate">
                                                        <span className="material-icons">check_circle</span>
                                                        {(exp.validationRate * 100).toFixed(1)}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Daily Activity Chart */}
                        {dailyActivity.length > 0 && (
                            <div className="detail-section">
                                <h3>
                                    <span className="material-icons">bar_chart</span>
                                    {t('taskDetail.dailyActivity')}
                                </h3>
                                <div className="activity-chart">
                                    {dailyActivity.slice(0, 7).reverse().map((day) => (
                                        <div key={day.date} className="chart-bar-container">
                                            <div className="chart-bar">
                                                <div
                                                    className="bar-success"
                                                    style={{
                                                        height: `${Math.min((day.success / Math.max(...dailyActivity.map(d => d.processed))) * 100, 100)}%`
                                                    }}
                                                    title={`${day.success} ${t('taskDetail.successful')}`}
                                                />
                                                <div
                                                    className="bar-failed"
                                                    style={{
                                                        height: `${Math.min((day.failed / Math.max(...dailyActivity.map(d => d.processed))) * 100, 100)}%`
                                                    }}
                                                    title={`${day.failed} ${t('taskDetail.failed')}`}
                                                />
                                            </div>
                                            <span className="chart-label">
                                                {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short' })}
                                            </span>
                                            <span className="chart-value">{day.processed}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'executions' && (
                    <div className="executions-tab">
                        {recentExecutions.length === 0 ? (
                            <div className="empty-state">
                                <span className="material-icons">history</span>
                                <p>{t('taskDetail.noExecutions')}</p>
                            </div>
                        ) : (
                            <div className="executions-list">
                                {recentExecutions.map((exec, idx) => (
                                    <div key={idx} className={`execution-item ${exec.status}`}>
                                        <div className="exec-status">
                                            <span className={`material-icons status-icon ${exec.status}`}>
                                                {exec.status === 'completed' ? 'check_circle' :
                                                    exec.status === 'failed' ? 'error' :
                                                        exec.status === 'running' ? 'sync' : 'play_circle'}
                                            </span>
                                        </div>
                                        <div className="exec-info">
                                            <div className="exec-header">
                                                <span className="exec-time">
                                                    {formatDate(exec.timestamp)}
                                                </span>
                                                <span className="exec-relative">
                                                    {formatRelativeTime(exec.timestamp)}
                                                </span>
                                            </div>
                                            <div className="exec-details">
                                                {exec.duration !== undefined && (
                                                    <span className="exec-detail">
                                                        <span className="material-icons">timer</span>
                                                        {formatDuration(exec.duration)}
                                                    </span>
                                                )}
                                                {exec.processedItems !== undefined && (
                                                    <span className="exec-detail">
                                                        <span className="material-icons">description</span>
                                                        {exec.processedItems} {t('taskDetail.items')}
                                                    </span>
                                                )}
                                            </div>
                                            {exec.errorMessage && (
                                                <div className="exec-error">
                                                    <span className="material-icons">warning</span>
                                                    {exec.errorMessage}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'documents' && (
                    <div className="documents-tab">
                        {recentDocuments.length === 0 ? (
                            <div className="empty-state">
                                <span className="material-icons">article</span>
                                <p>{t('taskDetail.noDocuments')}</p>
                            </div>
                        ) : (
                            <div className="documents-table-container">
                                <table className="documents-table">
                                    <thead>
                                        <tr>
                                            <th>{task.group?.toUpperCase() === 'SEARCH' ? t('taskDetail.url') : t('taskDetail.filename')}</th>
                                            <th>{t('taskDetail.processed')}</th>
                                            <th>{t('taskDetail.duration')}</th>
                                            <th>{t('taskDetail.validation')}</th>
                                            <th>{task.group?.toUpperCase() === 'SEARCH' ? t('taskDetail.pdfsFound') : t('taskDetail.fieldsFound')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentDocuments.map((doc) => (
                                            <tr key={doc._id} className={doc.experiment_error ? 'has-error' : ''}>
                                                <td className="filename-cell">
                                                    <span className="filename" title={doc.filename}>
                                                        {doc.filename.length > 40
                                                            ? `...${doc.filename.slice(-40)}`
                                                            : doc.filename}
                                                    </span>
                                                    {doc.language && (
                                                        <span className="lang-badge">{doc.language}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {doc.processing_timestamp
                                                        ? formatRelativeTime(doc.processing_timestamp)
                                                        : '-'}
                                                </td>
                                                <td>
                                                    {doc.processing_time_seconds
                                                        ? `${doc.processing_time_seconds.toFixed(1)}s`
                                                        : '-'}
                                                </td>
                                                <td>
                                                    {doc.experiment_error ? (
                                                        <span className="validation-badge error" title={doc.experiment_error}>
                                                            <span className="material-icons">error</span>
                                                            {t('taskDetail.error')}
                                                        </span>
                                                    ) : doc.validation_passed === true ? (
                                                        <span className="validation-badge passed">
                                                            <span className="material-icons">check</span>
                                                            {doc.validation_score !== undefined
                                                                ? `${(doc.validation_score * 100).toFixed(0)}%`
                                                                : t('taskDetail.passed')}
                                                        </span>
                                                    ) : doc.validation_passed === false ? (
                                                        <span className="validation-badge failed">
                                                            <span className="material-icons">close</span>
                                                            {t('taskDetail.failed')}
                                                        </span>
                                                    ) : (
                                                        <span className="validation-badge pending">
                                                            {t('taskDetail.pending')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{doc.fields_found ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskDetailView;
